import React, { useState, FormEvent } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Divider,
    Pagination,
    CircularProgress,
    Select,
    MenuItem,
    TextField,
    InputAdornment,
    Button,
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
} from '@mui/material';
import { HiOutlineSearch } from 'react-icons/hi';

interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  wallet: number;
  referral_commission: number; 
}

interface UsersTabProps {
    apiFetch: (url: string, options?: RequestInit) => Promise<any>;
    setError: (error: string | null) => void;
    showNotificationModal: (title: string, message: string, type: 'success' | 'error' | 'warning') => void;
    
    allUsers: User[];
    usersLoading: boolean;
    usersPage: number;
    usersTotalPages: number;
    onPageChange: (page: number) => void;
    searchResults: User[];
    setSearchResults: React.Dispatch<React.SetStateAction<User[]>>;
}

const WalletEditor: React.FC<{
    user: User;
    editingUserId: number | null;
    newWalletValue: string;
    setNewWalletValue: React.Dispatch<React.SetStateAction<string>>;
    setEditingUserId: React.Dispatch<React.SetStateAction<number | null>>;
    handleWalletSave: (e: FormEvent, userId: number) => Promise<void>;
}> = ({
    user,
    editingUserId,
    newWalletValue,
    setNewWalletValue,
    setEditingUserId,
    handleWalletSave,
}) => {
    return (
        <TableCell>
            {editingUserId === user.id ? (
                <Box component="form" onSubmit={(e) => handleWalletSave(e, user.id)} sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 150 }}>
                    <TextField
                        label="موجودی جدید ($)"
                        type="number"
                        value={newWalletValue}
                        onChange={(e) => setNewWalletValue(e.target.value)}
                        size="small"
                        inputProps={{ step: "0.01" }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button type="submit" size="small" variant="contained">ذخیره</Button>
                        <Button size="small" variant="outlined" onClick={() => setEditingUserId(null)}>لغو</Button>
                    </Box>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                    <Typography>{(user.wallet / 100).toFixed(2)}$</Typography>
                    <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={() => {
                            setEditingUserId(user.id);
                            setNewWalletValue(String(user.wallet / 100));
                        }}
                    >
                        ویرایش
                    </Button>
                </Box>
            )}
        </TableCell>
    );
};


const UsersTab: React.FC<UsersTabProps> = ({
    apiFetch,
    setError,
    showNotificationModal, 
    allUsers,
    usersLoading,
    usersPage,
    usersTotalPages,
    onPageChange,
    searchResults,
    setSearchResults,
}) => {

    const [searchLoading, setSearchLoading] = useState(false);
    
    const [editingUserId, setEditingUserId] = useState<number | null>(null);
    const [newWalletValue, setNewWalletValue] = useState('');

    const handleUserSearch = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const query = formData.get('query') as string;
        const type = formData.get('type') as string;
        if (!query || !type) return;

        setSearchLoading(true);
        setError(null);
        setSearchResults([]);
        try {
            const result = await apiFetch(`/search/user/${type}/${query}`);
            setSearchResults(result ? (Array.isArray(result) ? result : [result]) : []);
            if (!result || (Array.isArray(result) && result.length === 0)) {
                setError('کاربری با این مشخصات یافت نشد.');
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setSearchLoading(false);
        }
    };

    const handleUsersPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        onPageChange(value);
    };

    const handleWalletSave = async (e: FormEvent, userId: number) => {
        e.preventDefault();
        setError(null);

        const newAmountInCents = Math.round(parseFloat(newWalletValue) * 100);

        if (isNaN(newAmountInCents) || newAmountInCents < 0) {
            setError('لطفا مقدار عددی معتبر (بزرگتر یا مساوی صفر) وارد کنید.');
            return;
        }

        const payload = {
            wallet: newAmountInCents,
        };

        try {
            await apiFetch(`/admin/users/${userId}/wallet`, {
                method: 'PATCH',
                body: JSON.stringify(payload),
            });

            showNotificationModal(
                'موفقیت‌آمیز', 
                `کیف پول کاربر ${userId} با موفقیت به ${newWalletValue}$ به‌روزرسانی شد.`, 
                'success'
            );
            
            setEditingUserId(null); 

            onPageChange(usersPage); 
            
            if (searchResults.length > 0) {
                 setSearchResults(prevSearchResults => prevSearchResults.map(user => 
                    user.id === userId ? { ...user, wallet: newAmountInCents } : user
                 ));
            }

        } catch (err: any) {
            showNotificationModal(
                'خطا در عملیات', 
                err.message || 'خطا در به‌روزرسانی کیف پول رخ داد.', 
                'error'
            );
            setError(err.message || 'خطا در به‌روزرسانی کیف پول'); 
        }
    };

    return (
        <>
            <Typography variant="h5" sx={{ mb: 3 }}>
                جستجوی کاربر
            </Typography>
            <Paper component="form" className="management-card" onSubmit={handleUserSearch}>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: { xs: 'stretch', sm: 'center' },
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}
                >
                    <Select
                        name="type"
                        defaultValue="username"
                        sx={{
                            minWidth: { sm: 120 },
                            height: '56px',
                            backgroundColor: 'var(--background-color)',
                            color: 'var(--text-color)',
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '.MuiSelect-select': {
                                display: 'flex',
                                alignItems: 'center',
                                color: 'var(--text-color)'
                            }
                        }}
                    >
                        <MenuItem value="id">ایدی</MenuItem>
                        <MenuItem value="email">ایمیل</MenuItem>
                        <MenuItem value="username">نام کاربری</MenuItem>
                        <MenuItem value="phone">شماره</MenuItem>
                    </Select>
                    <TextField
                        name="query"
                        fullWidth
                        placeholder="عبارت مورد نظر را وارد کنید..."
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <HiOutlineSearch style={{ color: 'var(--text-color)' }} />
                                </InputAdornment>
                            )
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                height: '56px',
                                backgroundColor: 'var(--background-color)',
                                color: 'var(--text-color)'
                            },
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                        }}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        sx={{ px: 4, height: '56px' }}
                        disabled={searchLoading}
                    >
                        {searchLoading ? <CircularProgress size={24} /> : 'جستجو'}
                    </Button>
                </Box>
            </Paper>
            
            {searchResults.length > 0 && (
                <Card className="management-card">
                    <CardContent>
                        <Typography variant="h6" component="h3" gutterBottom>
                            نتیجه جستجو
                        </Typography>
                        {searchResults.map(user => (
                            <Box key={user.id} sx={{ fontSize: '1rem', color: 'var(--text-color)', borderBottom: '1px solid var(--border-color)', pb: 2, mb: 2 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}><strong>ID:</strong> {user.id}</Grid>
                                    <Grid item xs={12} sm={6}><strong>نام کاربری:</strong> {user.username}</Grid>
                                    <Grid item xs={12} sm={6}><strong>نام:</strong> {`${user.first_name || ''} ${user.last_name || ''}`}</Grid>
                                    <Grid item xs={12} sm={6}><strong>ایمیل:</strong> {user.email}</Grid>
                                    <Grid item xs={12} sm={6}><strong>تلفن:</strong> {user.phone}</Grid>
                                                                        <Grid item xs={12}>
                                        {editingUserId === user.id ? (
                                            <Box component="form" onSubmit={(e) => handleWalletSave(e, user.id)} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                <TextField
                                                    label="موجودی جدید ($)"
                                                    type="number"
                                                    value={newWalletValue}
                                                    onChange={(e) => setNewWalletValue(e.target.value)}
                                                    size="small"
                                                    inputProps={{ step: "0.01" }}
                                                />
                                                <Button type="submit" size="small" variant="contained">ذخیره</Button>
                                                <Button size="small" variant="outlined" onClick={() => setEditingUserId(null)}>لغو</Button>
                                            </Box>
                                        ) : (
                                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                                <Typography><strong>موجودی کیف پول:</strong> {user.wallet / 100}$</Typography>
                                                <Button size="small" variant="outlined" onClick={() => {
                                                    setEditingUserId(user.id);
                                                    setNewWalletValue(String(user.wallet / 100));
                                                }}>
                                                    ویرایش
                                                </Button>
                                            </Box>
                                        )}
                                    </Grid>

                                </Grid>
                            </Box>
                        ))}
                    </CardContent>
                </Card>
            )}

            <Divider sx={{ my: 4 }} />

            <Typography variant="h5" sx={{ mb: 3 }}>
                لیست تمام کاربران
            </Typography>

            {usersLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            ) : allUsers.length > 0 ? (
                <>
                    <TableContainer component={Paper} className="management-card">
                        <Table sx={{ minWidth: 650 }} aria-label="users table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>نام کاربری</TableCell>
                                    <TableCell>نام کامل</TableCell>
                                    <TableCell>ایمیل</TableCell>
                                    <TableCell>تلفن</TableCell>
                                    <TableCell>موجودی کیف پول</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allUsers.map((user) => (
                                    <TableRow
                                        key={user.id}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell component="th" scope="row">{user.id}</TableCell>
                                        <TableCell>{user.username}</TableCell>
                                        <TableCell>{`${user.first_name || ''} ${user.last_name || ''}`}</TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>{user.phone}</TableCell>
                                        
                                        {/* استفاده از WalletEditor برای نمایش/ویرایش کیف پول */}
                                        <WalletEditor
                                            user={user}
                                            editingUserId={editingUserId}
                                            newWalletValue={newWalletValue}
                                            setNewWalletValue={setNewWalletValue}
                                            setEditingUserId={setEditingUserId}
                                            handleWalletSave={handleWalletSave}
                                        />
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <Pagination
                            count={usersTotalPages}
                            page={usersPage}
                            onChange={handleUsersPageChange}
                            color="primary"
                            shape="rounded"
                        />
                    </Box>
                </>
            ) : (
                <Typography>هیچ کاربری برای نمایش وجود ندارد.</Typography>
            )}
        </>
    );
};

export default UsersTab;